/**
 * FRONT-2598 — speaker notes are PLAIN TEXT in the editor and markup in the deck.
 *
 * The deck stores notes as a fragment of HTML (`<aside class="deck-notes">`),
 * and an Eddy-authored deck may carry `<br>`, `<p>` or entities there. The
 * textarea shows text, so a stored value is converted here for display; what the
 * person types goes to the server as text and is escaped there
 * (`notesTextToHtml`, src/artifacts/manual-edit.ts) — never parsed.
 */

type ParsedNotes = {
  body: { textContent: string | null };
  querySelectorAll(selector: string): ArrayLike<{ replaceWith(...n: unknown[]): void; append(...n: unknown[]): void }>;
  createTextNode(text: string): unknown;
};

function defaultParse(html: string): ParsedNotes | null {
  // DOMParser builds an inert document: no script runs and nothing is attached to Eddy's page.
  return typeof DOMParser === 'undefined' ? null : (new DOMParser().parseFromString(html, 'text/html') as unknown as ParsedNotes);
}

const BLOCK_TAGS = 'p, div, li, h1, h2, h3, h4, h5, h6';

/** Used only where there is no DOMParser (the plain-node unit suite). Tags are removed BEFORE entities are decoded. */
function fallbackPlain(html: string): string {
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|li|h[1-6])\s*>/gi, '\n')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;|&apos;/gi, "'")
    .replace(/&amp;/gi, '&');
}

/** Stored notes as the text a person edits: `<br>` is a newline, other tags are dropped, entities are decoded. */
export function notesToPlainText(html: string, parse: (html: string) => ParsedNotes | null = defaultParse): string {
  if (!html) return '';
  let text: string;
  try {
    const doc = parse(html);
    if (doc) {
      Array.from(doc.querySelectorAll('br')).forEach((br) => br.replaceWith(doc.createTextNode('\n')));
      Array.from(doc.querySelectorAll(BLOCK_TAGS)).forEach((el) => el.append(doc.createTextNode('\n')));
      text = doc.body.textContent ?? '';
    } else {
      text = fallbackPlain(html);
    }
  } catch {
    text = fallbackPlain(html);
  }
  return text.replace(/\n+$/, '');
}

/** The stored form of typed notes — the client's mirror of the server's `notesTextToHtml`, used only to guess the new deck when it cannot be re-read. */
export function notesPlainToHtml(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\r\n?|\n/g, '<br>');
}
